import React from 'react';
import { Box, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import dayjs from 'dayjs';
import { useMobileBreakpoint } from '@repo/utils';

/**
 * Horizontal alignment options for the version string within the AppVersionInfo layout.
 *
 * Used to align version text within the layout.
 */
type AppVersionInfoAlign = 'start' | 'center' | 'end';
/**
 * Layout direction for stacking the version info and copyright lines.
 */
type AppVersionInfoDirection = 'row' | 'column';

/**
 * Props for the AppVersionInfo component that renders the application version and copyright notice.
 */
export interface AppVersionInfoProps {
  version?: string;
  onVersionClick?: () => void;
  compact?: boolean;
  align?: AppVersionInfoAlign;
  direction?: AppVersionInfoDirection;
  color?: string;
  copyrightMarginRight?: number | string;
}

const getTextAlignment = (align: AppVersionInfoAlign): 'left' | 'center' | 'right' => {
  switch (align) {
    case 'center': return 'center';
    case 'end':    return 'right';
    default:       return 'left';
  }
};

/**
 * Renders the application version string with an optional clickable link and a copyright notice, with layout differences for mobile and desktop.
 */
export function AppVersionInfo({
  version,
  onVersionClick,
  compact = false,
  color,
  copyrightMarginRight,
  align = 'center',
}: AppVersionInfoProps) {

  const isMobile = useMobileBreakpoint();

  const versionLabel = version ? `v${version}` : null;
  const textAlign = getTextAlignment(align);

  const versionContent = versionLabel ? (
    onVersionClick ? (
      <UnstyledButton
        aria-label={`Open release notes for ${versionLabel}`}
        onClick={onVersionClick}
        style={{ minWidth: 0, maxWidth: '100%', color: color ?? 'inherit', cursor: 'pointer' }}
      >
        <Text
          fz='xs'
          inherit
          span
          style={{
            display: 'block',
            textAlign,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
          }}
        >
          {versionLabel}
        </Text>
      </UnstyledButton>
    ) : (
      <Text
        fz='xs'
        c={color}
        style={{ textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}
      >
        {versionLabel}
      </Text>
    )
  ) : null;

  if (compact) {
    return <Box pl={5} pr={5}>{versionContent}</Box>;
  }

  const copyrightText = `Copyright © ${dayjs().year()}.`;
  const reservedText = 'AgReserves Inc. All rights reserved';

  // ── Mobile: everything on one line ───────────────────────────────────────
  if (isMobile) {
    return (
      <Group justify="space-between" wrap="nowrap" pr={30} pl={30} pb={6} gap={2}>
        {versionContent}
        <Text fz="xs" c={color} style={{ whiteSpace: 'nowrap' }}>
          {copyrightText}
        </Text>
        <Text fz="xs" c={color} style={{ whiteSpace: 'nowrap' }}>
          {reservedText}
        </Text>
      </Group>
    );
  }

  // ── Desktop: original layout ──────────────────────────────────────────────
  return (
    <Stack m={'auto'} w={'fit-content'} gap={'xs'}>
      <Group justify={'space-between'}>
        {versionContent}
        <Text
          fz='xs'
          c={color}
          ta={textAlign}
          mr={copyrightMarginRight}
          style={{
            textAlign,
            overflowWrap: 'anywhere'
          }}
        >
          {`Copyright © ${dayjs().year()}. `}
        </Text>
      </Group>
      <Text
        w={'100%'}
        fz='xs'
        ta={'right'}
        c={color}
        mr={copyrightMarginRight}
        style={{
          textAlign,
          overflowWrap: 'anywhere'
        }}
      >
        AgReserves Inc. All rights reserved
      </Text>
    </Stack>
  );
}
